import { Controller, useFormContext, get } from "react-hook-form";
import { cn } from "@/utils/cn";
import { cva } from "class-variance-authority";
import {
    Field,
    Label,
    Description,
    Checkbox as HeadlessCheckbox,
} from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/16/solid";

const fieldVariants = cva("flex flex-col", {
    variants: {
        size: {
            default: "gap-2",
            sm: "gap-3",
            lg: "gap-4",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "default",
    },
});

const containerVariants = cva("flex w-fit items-center gap-2", {
    variants: {
        variant: {
            primary: "text-darkgreen-500",
            secondary: "text-darkgreen-500",
            success: "text-lime-700",
            danger: "text-red-400",
        },
        labelPlacement: {
            start: "flex-row",
            end: "flex-row-reverse",
            top: "flex-col",
            bottom: "flex-col-reverse",
        },
        size: {
            default: "text-base",
            sm: "text-sm",
            lg: "text-lg",
        },
    },
    defaultVariants: {
        labelPlacement: "start",
        size: "default",
    },
});

const checkBoxVariants = cva(
    "group shrink-0 rounded-md bg-white ring-1 ring-white/15 outline-1 ring-inset focus-visible:shadow-[0_0_0_0.25rem] focus-visible:outline-2 focus-visible:-outline-offset-2 data-checked:outline-0",
    {
        variants: {
            variant: {
                primary:
                    "shadow-darkgreen-500/25 outline-gray-400 data-checked:bg-darkgreen-500",
                secondary:
                    "shadow-emerald-950/25 outline-gray-400 data-checked:bg-emerald-950",
                success:
                    "shadow-lime-500/25 outline-lime-500 data-checked:bg-lime-500",
                danger: "shadow-red-500/25 outline-red-500 data-checked:bg-red-500",
            },
            size: {
                default: "size-6 p-0.25",
                sm: "size-5.5 p-0.25",
                lg: "p-0.2 size-6.5",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
        },
    },
);

const checkIconVariants = cva("hidden group-data-checked:block", {
    variants: {
        variant: {
            primary: "fill-emerald-950",
            secondary: "fill-darkgreen-500",
            success: "fill-darkgreen-500",
            danger: "fill-white",
        },
        size: {
            default: "size-5.5",
            sm: "size-5",
            lg: "size-6.5",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "default",
    },
});

export const Checkbox = ({
    label,
    description,
    name,
    value,
    className,
    disabled,
    hideLabel = false,
    variant,
    labelPlacement,
    size,
    onChange,
    isMulti = false,
}) => {
    const {
        control,
        formState: { errors /*isSubmitted*/ },
    } = useFormContext();

    const hasError = get(errors, name);
    const formStateVariant = hasError
        ? "danger"
        : /*: isSubmitted
          ? "success"*/
          variant || "primary";

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={isMulti ? [] : false}
            render={({ field }) => (
                <Field
                    disabled={disabled}
                    className={cn(
                        fieldVariants({
                            size,
                        }),
                    )}
                >
                    <div
                        className={cn(
                            containerVariants({
                                variant: formStateVariant,
                                labelPlacement,
                                size,
                                className,
                            }),
                        )}
                    >
                        <Label
                            className={cn(
                                "inline-block select-none data-disabled:opacity-50",
                                hideLabel ? "sr-only" : "",
                                description && "font-semibold",
                            )}
                        >
                            {label}
                        </Label>
                        <HeadlessCheckbox
                            checked={
                                isMulti
                                    ? field.value?.includes(value)
                                    : field.value
                            }
                            onChange={(checked) => {
                                if (isMulti) {
                                    const current = field.value || [];

                                    const newValue = checked
                                        ? [...current, value]
                                        : current.filter((v) => v !== value);

                                    field.onChange(newValue);
                                    onChange?.(newValue);
                                } else {
                                    field.onChange(checked);
                                    onChange?.(checked);
                                }
                            }}
                            className={cn(
                                checkBoxVariants({
                                    variant: formStateVariant,
                                    size,
                                }),
                            )}
                        >
                            <CheckIcon
                                className={cn(
                                    checkIconVariants({
                                        variant: formStateVariant,
                                        size,
                                    }),
                                )}
                            />
                        </HeadlessCheckbox>
                    </div>
                    {description && (
                        <Description className="text-sm font-medium text-neutral-500 select-none">
                            {description}
                        </Description>
                    )}
                </Field>
            )}
        />
    );
};
